import type {
  ApiResponse, ApiErrorCode, Cart, Category, HomePayload, Order,
  ProductCard, Product, CategoryFilters, Me, NotificationPreferences,
  CreateOrderInput, PriceChange, ApiMeta,
} from '@alkor/types';
import { initDataHeader } from './telegram';

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787/api/v1';

export class ApiClientError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function headers(): HeadersInit {
  const h: Record<string, string> = { 'content-type': 'application/json' };
  const initData = initDataHeader();
  if (initData) {
    h['x-telegram-init-data'] = initData;
  } else if (import.meta.env.DEV) {
    // Разработка в обычном браузере: сервер принимает этот заголовок,
    // только когда ALLOW_DEV_AUTH=true. В проде такого режима нет.
    h['x-dev-user'] = String(import.meta.env.VITE_DEV_USER ?? 777001);
  }
  return h;
}

async function request<T>(path: string, init?: RequestInit): Promise<{ data: T; meta?: ApiMeta }> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers: { ...headers(), ...init?.headers } });
  } catch {
    throw new ApiClientError('INTERNAL', 'Нет связи с сервером. Проверьте интернет.', 0);
  }

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError('INTERNAL', 'Сервер вернул неожиданный ответ', res.status);
  }

  if (!body.ok) {
    throw new ApiClientError(body.error.code, body.error.message, res.status, body.error.details);
  }
  return { data: body.data, meta: body.meta };
}

const get = <T>(path: string) => request<T>(path).then((r) => r);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

export interface ProductQuery {
  category?: string;
  q?: string;
  brand?: string;
  available?: boolean;
  /** Метка: sale — акции, hit — хиты */
  badge?: string;
  priceMin?: number;
  priceMax?: number;
  sort?: string;
  page?: number;
  limit?: number;
  /** «Кратность~4-12x» или «Макс. глубина~50..200». Параметр повторяемый. */
  spec?: string[];
}

function qs(params: ProductQuery): string {
  const sp = new URLSearchParams();
  if (params.category) sp.set('category', params.category);
  if (params.q) sp.set('q', params.q);
  if (params.brand) sp.set('brand', params.brand);
  if (params.available) sp.set('available', 'true');
  if (params.badge) sp.set('badge', params.badge);
  if (params.priceMin !== undefined) sp.set('price_min', String(params.priceMin));
  if (params.priceMax !== undefined) sp.set('price_max', String(params.priceMax));
  if (params.sort) sp.set('sort', params.sort);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  for (const s of params.spec ?? []) sp.append('spec', s);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface OrderConflict {
  serverTotal: number;
  clientTotal: number;
  changes: PriceChange[];
}

export const api = {
  home: () => get<HomePayload>('/catalog/home').then((r) => r.data),
  categories: () => get<Category[]>('/catalog/categories').then((r) => r.data),

  products: (params: ProductQuery) =>
    get<ProductCard[]>(`/catalog/products${qs(params)}`).then((r) => ({
      items: r.data,
      meta: r.meta,
    })),

  product: (slug: string) => get<Product>(`/catalog/products/${slug}`).then((r) => r.data),

  related: (slug: string) =>
    get<{ accessories: ProductCard[]; similar: ProductCard[] }>(
      `/catalog/products/${slug}/related`,
    ).then((r) => r.data),

  filters: (slug: string) => get<CategoryFilters>(`/catalog/filters/${slug}`).then((r) => r.data),

  me: () => get<Me>('/me').then((r) => r.data),

  cart: () => get<Cart>('/cart').then((r) => r.data),
  cartAdd: (productId: string, quantity = 1) =>
    post<Cart>('/cart/items', { productId, quantity }).then((r) => r.data),
  cartSetQty: (itemId: string, quantity: number) =>
    patch<Cart>(`/cart/items/${itemId}`, { quantity }).then((r) => r.data),
  cartRemove: (itemId: string) => del<Cart>(`/cart/items/${itemId}`).then((r) => r.data),
  cartClear: () => del<Cart>('/cart').then((r) => r.data),

  createOrder: (input: CreateOrderInput) => post<Order>('/orders', input).then((r) => r.data),
  orders: () => get<Order[]>('/orders').then((r) => r.data),
  order: (number: string) => get<Order>(`/orders/${number}`).then((r) => r.data),
  repeatOrder: (number: string) =>
    post<{ added: string[]; unavailable: string[]; cart: Cart }>(
      `/orders/${number}/repeat`,
    ).then((r) => r.data),

  favorites: () => get<ProductCard[]>('/favorites').then((r) => r.data),
  favoriteAdd: (productId: string) => post(`/favorites/${productId}`).then(() => true),
  favoriteRemove: (productId: string) => del(`/favorites/${productId}`).then(() => false),

  recentlyViewed: () => get<ProductCard[]>('/recently-viewed').then((r) => r.data),

  subscriptions: () => get<{ stock: string[] }>('/subscriptions').then((r) => r.data),
  subscribeStock: (productId: string) =>
    post(`/subscriptions/stock/${productId}`).then(() => true),
  unsubscribeStock: (productId: string) =>
    del(`/subscriptions/stock/${productId}`).then(() => false),

  notifications: () => get<NotificationPreferences>('/me/notifications').then((r) => r.data),
  setNotifications: (patchBody: Partial<NotificationPreferences>) =>
    patch<{ updated: string[] }>('/me/notifications', patchBody).then((r) => r.data),

  createLead: (body: {
    message: string;
    source: 'bot' | 'miniapp_empty_search' | 'miniapp_product' | 'miniapp_cart';
    contextProductId?: string | null;
    contextQuery?: string | null;
  }) => post<{ leadId: string }>('/leads', body).then((r) => r.data),

  sendEvents: (events: unknown[]) =>
    fetch(`${BASE}/events`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => undefined),
};
